import { inject, Injectable } from '@angular/core';
import { SaveServiceInterface } from '../save.service';
import { BatchWeighingStoreCommand, StoreBatchWeighingSaveInfo } from '../../storage/data-store/model/store-batch-weighing-save-info';
import { SyncStoreWriter } from '../sync-store/sync-store-writer';
import { SaveToSyncMapper } from '../../mappers/sync-store/save-to-sync.mapper';
import { RegistrationStoreWriterService } from './registration-store-writer.service';
import { TimeService } from '../time.service';
import { UserService } from '../user.service';
import { UuidService } from '../uuid.service';
import { AnimalWeighingSaveInfoViewModel } from '../../viewmodels/shared/animal-weighing-save-info.viewmodel';
import { ViewModelsToDataStoreMapper } from '../../mappers/data-store/viewmodels-to-data-store.mapper';
import { RemovalSaveInfoViewModel } from '../../viewmodels/shared/removal-save-info.viewmodel';
import { SyncStoreSaveData } from '../../storage/sync-store/update-data/sync-store-save-data';
import { SyncStoreUpdateRemoval } from '../../storage/sync-store/update-data/sync-store-update-removal';
import { SyncStoreLastServerSyncInfo } from '../../storage/sync-store/model/sync-store-last-server-sync-info';

@Injectable({
  providedIn: 'root',
})
export class SaveLocalService implements SaveServiceInterface {
  private readonly syncWriter = inject(SyncStoreWriter);
  private readonly syncMapper = inject(SaveToSyncMapper);
  private readonly dataMapper = inject(ViewModelsToDataStoreMapper);
  private readonly saveRegService = inject(RegistrationStoreWriterService);
  private readonly timeService = inject(TimeService);
  private readonly userInfoService = inject(UserService);
  private readonly uuidService = inject(UuidService);

  constructor() {}

  private saveAnimalWeighingInfo(batchWeighingSaveInfo: StoreBatchWeighingSaveInfo, saveTime: Date) {
    const animalWeighingsSync = this.syncMapper.mapAnimalWeighingSyncCommand(batchWeighingSaveInfo, saveTime);
    this.syncWriter.persistAnimalWeighings(animalWeighingsSync);
    this.saveRegService.storeAnimalWeighings(batchWeighingSaveInfo);
  }

  private saveBatchWeighingInfo(batchWeighingSaveInfo: StoreBatchWeighingSaveInfo, saveTime: Date) {
    const batchWeighingSync = this.syncMapper.mapBatchWeighingSyncCommand(batchWeighingSaveInfo, saveTime);
    this.syncWriter.persistBatchWeighing(batchWeighingSync);
    this.saveRegService.storeBatchWeighing(batchWeighingSaveInfo);
  }

  SaveBatchWeighing(
    weighingSaveInfos: AnimalWeighingSaveInfoViewModel[],
    batchWeighingStoreCommand: BatchWeighingStoreCommand | undefined
  ) : void {
    const batchWeighingSaveInfo = this.dataMapper.mapToBatchWeighingSaveInfo(weighingSaveInfos, batchWeighingStoreCommand);
    const saveTimeNow = this.timeService.getNow();
    const hasBatchWeighingData = batchWeighingSaveInfo.hasBatchWeighingData();
    const hasAnimalWeighingData = batchWeighingSaveInfo.hasAnimalWeighingData();

    if (!hasBatchWeighingData && !hasAnimalWeighingData) {
      throw new Error('Nothing to save. BatchWeighingStoreCommand and AnimalWeighings are both undefined or empty');
    }
    if (hasAnimalWeighingData) {
      this.saveAnimalWeighingInfo(batchWeighingSaveInfo, saveTimeNow);
    }
    if (hasBatchWeighingData) {
      this.saveBatchWeighingInfo(batchWeighingSaveInfo, saveTimeNow);
    }
  }

  saveRemovals(
    removalSaveInfos: RemovalSaveInfoViewModel[],
  ) : void {
    if (removalSaveInfos.length === 0) {
      console.debug('No removals to save.');
      return;
    }

    const saveTimeNow = this.timeService.getNow();
    const syncData = new SyncStoreSaveData<SyncStoreUpdateRemoval>(
      this.uuidService.getNewUuid().asString(),
      saveTimeNow,
      this.userInfoService.getUser(),
      removalSaveInfos.map((item) => new SyncStoreUpdateRemoval(
        item.id,
        new SyncStoreLastServerSyncInfo(null, saveTimeNow, null),
        item.removal.date,
        item.removal.pen,
        item.removal.numberOfAnimals,
        item.removal.weight,
        item.removal.code,
        item.removal.reason
      ))
    );

    this.syncWriter.persistRemovals(syncData);
    this.saveRegService.storeRemovals(removalSaveInfos);
    console.debug('Saving removals:', removalSaveInfos);
  }
}
